import type { Edge, Node } from "@xyflow/react";
import type { PageOnSubmitActionType } from "@/types/pageFormSubmit";
import type { CreateLeadNodeData, PageNodeData } from "./types";

export interface FlowGraphPage {
  _id: string;
  route: string;
  formOnSubmit?: Record<string, PageOnSubmitActionType[]>;
}

const NODE_X_STEP = 340;
const NODE_Y = 80;

function pagePath(route: string): string {
  return route.startsWith("/") ? route : `/${route}`;
}

function pageHref({ baseUrl, route }: { baseUrl: string; route: string }): string {
  return `${baseUrl.replace(/\/$/, "")}${pagePath(route)}`;
}

export function buildFlowGraphFromFormOnSubmit({
  startPage,
  pagesById,
  baseUrl,
}: {
  startPage: FlowGraphPage;
  pagesById: Record<string, FlowGraphPage>;
  baseUrl: string;
}): {
  nodes: Node<PageNodeData | CreateLeadNodeData>[];
  edges: Edge[];
} {
  const nodes: Node<PageNodeData | CreateLeadNodeData>[] = [];
  const edges: Edge[] = [];

  const startPageNodeId = `page-${startPage._id}`;
  nodes.push({
    id: startPageNodeId,
    type: "page",
    position: { x: 0, y: NODE_Y },
    data: {
      pageId: startPage._id,
      isEntry: true,
      href: pageHref({ baseUrl, route: startPage.route }),
      path: pagePath(startPage.route),
    },
  });

  let previousNodeId = startPageNodeId;
  let previousEdgeType: "submit" | "then" = "submit";
  let x = NODE_X_STEP;
  let actionIndex = 0;

  const formOnSubmit = startPage.formOnSubmit ?? {};
  const formIds = Object.keys(formOnSubmit);

  for (const formId of formIds) {
    const actions = formOnSubmit[formId] ?? [];

    for (const action of actions) {
      if (action.type === "CreateLead") {
        const nodeId = `create-lead-${formId}-${actionIndex}`;
        nodes.push({
          id: nodeId,
          type: "createLead",
          position: { x, y: NODE_Y },
          data: {},
        });
        edges.push({
          id: `${previousNodeId}-${nodeId}`,
          source: previousNodeId,
          target: nodeId,
          type: previousEdgeType,
        });
        previousNodeId = nodeId;
        previousEdgeType = "then";
        x += NODE_X_STEP;
        actionIndex += 1;
        continue;
      }

      if (action.type === "GoTo" && action.pageId) {
        const targetPage = pagesById[String(action.pageId)];
        if (!targetPage) {
          actionIndex += 1;
          continue;
        }

        const nodeId = `page-${targetPage._id}`;
        const existingNode = nodes.find((node) => node.id === nodeId);
        if (!existingNode) {
          nodes.push({
            id: nodeId,
            type: "page",
            position: { x, y: NODE_Y },
            data: {
              pageId: targetPage._id,
              href: pageHref({ baseUrl, route: targetPage.route }),
              path: pagePath(targetPage.route),
            },
          });
          x += NODE_X_STEP;
        }

        edges.push({
          id: `${previousNodeId}-${nodeId}`,
          source: previousNodeId,
          target: nodeId,
          type: previousEdgeType,
          data: { hideLabel: true },
        });
        previousNodeId = nodeId;
        previousEdgeType = "then";
        actionIndex += 1;
      }
    }
  }

  return { nodes, edges };
}

export function collectGoToPageIds(page: FlowGraphPage): string[] {
  const ids = new Set<string>();
  const formOnSubmit = page.formOnSubmit ?? {};

  for (const formId of Object.keys(formOnSubmit)) {
    for (const action of formOnSubmit[formId] ?? []) {
      if (action.type === "GoTo" && action.pageId) {
        ids.add(String(action.pageId));
      }
    }
  }

  return [...ids];
}
